import { useCallback, useEffect, useRef, useState } from 'react'
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import Icon from '../Common/Icon'
import Menu from '../Common/Menu'
import {
  captureViewportCanvas,
  isPageTooLargeToAutoCapture,
} from '../FeedbackWidget/hooks/useScreenshotCapture'
import styles from './DynamicEnvFeedback.module.scss'
import FeedbackPopover from './components/FeedbackPopover'
import { drawClickMarker, IPoint } from './highlight'
import { loadTargets } from './targetsCache'

interface IOpenFeedback {
  point: IPoint
  screenshot: Promise<string | null>
}

// Text fields keep the browser's menu (paste, spelling), as does Shift+right-click anywhere.
const keepsNativeMenu = (event: MouseEvent): boolean =>
  event.defaultPrevented ||
  event.shiftKey ||
  (event.target instanceof Element &&
    Boolean(
      event.target.closest('input, textarea, [contenteditable="true"], [data-feedback-widget]'),
    ))

// Dynamic envs only: right-click anywhere to file feedback on the PR's ACT ticket in prod.
const DynamicEnvFeedback = () => {
  const [menuPoint, setMenuPoint] = useState<IPoint | null>(null)
  const [feedback, setFeedback] = useState<IOpenFeedback | null>(null)
  const chosenPointRef = useRef<IPoint | null>(null)

  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      if (keepsNativeMenu(event)) return
      event.preventDefault()
      setMenuPoint({ x: event.clientX, y: event.clientY })
      // Started now so the ticket is usually known by the time the popover opens; it shows any error.
      loadTargets().catch((): undefined => undefined)
    }
    document.addEventListener('contextmenu', handleContextMenu)
    return () => document.removeEventListener('contextmenu', handleContextMenu)
  }, [])

  const handleCloseMenu = useCallback(() => setMenuPoint(null), [])

  const handleAddFeedback = useCallback(() => {
    chosenPointRef.current = menuPoint
    setMenuPoint(null)
  }, [menuPoint])

  // Captures once the menu has faded out, so the screenshot shows the page and not the menu.
  const handleMenuExited = useCallback(() => {
    const point = chosenPointRef.current
    chosenPointRef.current = null
    if (!point) return
    // A page too large to render freezes the tab; the popover offers Select area instead.
    const screenshot = isPageTooLargeToAutoCapture()
      ? Promise.resolve<string | null>(null)
      : captureViewportCanvas()
          .then((canvas): string | null => (canvas ? drawClickMarker(canvas, point) : null))
          .catch((error: unknown): null => {
            // Feedback still files without a screenshot, as it does when the capture itself fails.
            // eslint-disable-next-line no-console
            console.error('Screenshot: marking the click failed', error)
            return null
          })
    setFeedback({ point, screenshot })
  }, [])

  const handleCloseFeedback = useCallback(() => setFeedback(null), [])

  return (
    <>
      <Menu
        anchorPosition={menuPoint && { top: menuPoint.y, left: menuPoint.x }}
        onClose={handleCloseMenu}
        onExited={handleMenuExited}
      >
        <MenuItem onClick={handleAddFeedback}>
          <ListItemIcon className={styles.menuIcon}>
            <Icon name="message-plus" size="sm" />
          </ListItemIcon>
          <ListItemText secondary="Shift+right-click for the browser menu">
            Add feedback
          </ListItemText>
        </MenuItem>
      </Menu>
      {feedback && (
        <FeedbackPopover
          point={feedback.point}
          screenshot={feedback.screenshot}
          onClose={handleCloseFeedback}
        />
      )}
    </>
  )
}

export default DynamicEnvFeedback
